import { Either, NonEmptyArray, Option, ReadonlyArray } from "@morphism/fp";
import React from "react";
import { useReactToPrint } from "react-to-print";
import styled from "styled-components";

import { Asset } from "~/Asset";
import { Button } from "~/ButtonV2";
import * as Color from "~/Color";
import * as Driver from "~/Driver";
import { DVIR } from "~/DVIR";
import * as Icon from "~/Icon";
import { ID } from "~/ID";
import { Media } from "~/Media";
import { NotApplicable } from "~/NotApplicable";
import { Route as GenericRoute } from "~/Route";
import { Signature } from "~/Signature";
import { Time } from "~/Time";
import { WorkOrder } from "~/WorkOrder";

export function Print({ dvir }: { readonly dvir: DVIR }) {
  const [isPrinting, setIsPrinting] = React.useState(false);
  const ref = React.useRef(null);
  const print = useReactToPrint({
    content: () => ref.current,
    onBeforeGetContent: () => setIsPrinting(true),
    onAfterPrint: () => setIsPrinting(false),
    pageStyle: "background: white !important;",
  });

  return (
    <>
      <Button
        type="default"
        icon={<Icon.Print />}
        disabled={isPrinting}
        loading={isPrinting}
        onClick={print}
      >
        Print DVIR
      </Button>
      <div style={{ display: "none" }}>
        <div ref={ref}>
          <Print.Title>
            <Time.DateTime
              Time={() => null}
              Separator={() => null}
              Date={(dateTime) => <>{Time.DateTime.toDateOnly(dateTime)}</>}
              {...dvir.lifecycle.submitted}
            />
            <br />
            <Driver.Name.Component
              {...Either.getOrElseW(() => undefined)(dvir.driver)}
              noLink
            />
            <br />
            <span>
              Created: {Time.DateTime.toDateOnly(dvir.lifecycle.created)}{" "}
              {Time.DateTime.toHMM(dvir.lifecycle.created)}
            </span>
          </Print.Title>
          <Print.Defects dvir={dvir} />
        </div>
      </div>
    </>
  );
}

export namespace Print {
  const spacing = () => "0.1in";
  const border = () => `0.02in solid ${Color.translucentBlack(0.3)}`;
  const borderRadius = () => "0.04in";

  export namespace Route {
    export const href = ({ id }: ID) => `/dvirs/${id}/print`;
    export const definition = () => href({ id: "id:" });
    export const use = (id: ID) => GenericRoute.use(href(id));
  }

  export const Title = styled.h1`
    font-size: 1.2em;
    font-weight: normal;
    & * {
      font-weight: normal;
    }
  `;

  export function Defects({ dvir }: { readonly dvir: DVIR }) {
    return pipe(
      dvir.defects,
      NonEmptyArray.fromReadonlyArray,
      Option.fold(
        () => <Defect dvir={dvir} {...DVIR.Defect.none(dvir)} />,
        flow(
          ReadonlyArray.map(({ defect }) => (
            <Defect key={defect.id} dvir={dvir} defect={defect} />
          )),
          (defects) => <>{defects}</>
        )
      ),
      (defects) => <Defects.Container>{defects}</Defects.Container>
    );
  }

  export namespace Defects {
    export const Container = styled.div`
      padding-top: 0.25in;
    `;
  }

  export function Defect({
    dvir,
    defect,
  }: { readonly dvir: DVIR } & DVIR.Defect) {
    return (
      <Defect.Container>
        <Defect.Title dvir={dvir} />
        <Defect.Grid>
          <Defect.Grid.Row>
            <Defect.Grid.Item>
              <Defect.Grid.Item.Title>Vehicle</Defect.Grid.Item.Title>
              {pipe(
                defect.asset,

                Either.fold(
                  () => <NotApplicable />,
                  (asset) =>
                    asset ? <>{Asset.name(asset)}</> : <NotApplicable />
                )
              )}
            </Defect.Grid.Item>
            <Defect.Grid.Item>
              <Defect.Grid.Item.Title>Defect</Defect.Grid.Item.Title>
              {defect.type.name}
            </Defect.Grid.Item>
            <Defect.Grid.Item>
              <Defect.Grid.Item.Title>Status</Defect.Grid.Item.Title>
              {defect.resolution ? (
                defect.resolution.type === "N/A" ? (
                  <>N/A</>
                ) : (
                  <>Resolved</>
                )
              ) : (
                <>Unresolved</>
              )}
            </Defect.Grid.Item>
          </Defect.Grid.Row>
          <Defect.Grid.Row>
            <Defect.Grid.Item>
              <Defect.Grid.Item.Title>Work Order</Defect.Grid.Item.Title>
              {pipe(
                Option.fromNullable(defect?.workOrder),
                Option.fold(
                  () => <NotApplicable />,
                  Either.fold(
                    () => <NotApplicable />,
                    (workOrder) => (
                      <WorkOrder.Link noDescription {...workOrder} />
                    )
                  )
                )
              )}
            </Defect.Grid.Item>
            <Defect.Grid.Item>
              <Defect.Grid.Item.Title>Timestamp</Defect.Grid.Item.Title>
              {pipe(
                Option.fromNullable(defect?.lifecycle.submitted),
                Option.getOrElse(() => dvir.lifecycle.submitted),
                (dateTime) => (
                  <div>
                    <div>{Time.DateTime.toDateOnly(dateTime)}</div>
                    <div>{Time.DateTime.toHMM(dateTime)}</div>
                  </div>
                )
              )}
            </Defect.Grid.Item>
          </Defect.Grid.Row>
          <Defect.Grid.Item>
            <DVIR.Defect.Comments print dvir={dvir} defect={defect} />
          </Defect.Grid.Item>
          <Defect.Grid.Item>
            <Defect.Grid.Item.Title>Driver signature</Defect.Grid.Item.Title>
            <Signature {...dvir} />
          </Defect.Grid.Item>
          <Defect.Grid.Item>
            <Defect.Grid.Item.Title>Mechanic signature</Defect.Grid.Item.Title>
            <Signature {...defect.resolution} />
          </Defect.Grid.Item>
          {defect?.images[0] && (
            <Defect.Grid.Item style={{ pageBreakBefore: "always" }}>
              <Defect.Grid.Item.Title>Images</Defect.Grid.Item.Title>
              {!defect ? (
                <NotApplicable>None available</NotApplicable>
              ) : (
                <Defect.Images defect={defect} />
              )}
            </Defect.Grid.Item>
          )}
        </Defect.Grid>
      </Defect.Container>
    );
  }

  export namespace Defect {
    export const Container = styled.div`
      margin-bottom: ${spacing()};
      border: ${border()};
      border-radius: ${borderRadius()};
      page-break-after: always;
    `;

    export function Title({ dvir }: { readonly dvir: DVIR }) {
      return (
        <Title.Container>
          <div>
            <DVIR.Type {...dvir} /> DVIR
          </div>
          <div>{DVIR.humanID(dvir)}</div>
        </Title.Container>
      );
    }

    export namespace Title {
      export const Container = styled.h1`
        margin: 0 0 ${spacing()} 0;
        padding: ${spacing()};
        display: flex;
        justify-content: space-between;

        border-bottom: ${border()};
        font-size: 1.2em;
        font-weight: normal;
      `;
    }

    export function Image(props: Image.Props) {
      return (
        <Image.Container>
          <Image.Count>
            {props.count} of {props.total}
          </Image.Count>
          <Image.Content>
            <img src={props.image.uri} />
          </Image.Content>
        </Image.Container>
      );
    }

    export namespace Image {
      export type Props = {
        readonly image: Media.Image;
        readonly count: number;
        readonly total: number;
      };

      export const Container = styled.div`
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 0.1in;
        page-break-inside: avoid;
      `;

      export const Count = styled.div`
        text-align: right;
      `;

      export const Content = styled.div`
        height: 300px;
        width: auto;
        & > img {
          height: 100%;
        }
      `;
    }

    export function Images({ defect }: DVIR.Defect) {
      return pipe(
        defect.images,
        ReadonlyArray.mapWithIndex((index, image) => (
          <Image
            key={index}
            count={index + 1}
            total={defect.images.length}
            image={image}
          />
        )),
        ReadonlyArray.chunksOf(2),
        ReadonlyArray.mapWithIndex((index, images) => (
          <Images.Container key={index}>{images}</Images.Container>
        )),
        (images) => <>{images}</>
      );
    }

    export namespace Images {
      export const Container = styled.div`
        display: flex;
        flex-direction: column;
        page-break-inside: avoid;
      `;
    }

    export function Grid({ children }: React.PropsWithChildren<unknown>) {
      return <Grid.Container>{children}</Grid.Container>;
    }

    export namespace Grid {
      export const Container = styled.div``;
      export const Row = styled.div`
        display: flex;
        & > div {
          flex-basis: 33.33%;
        }
      `;

      export function Item(
        props: React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>
      ) {
        return <Item.Container {...props} />;
      }

      export namespace Item {
        export const Container = styled.div`
          padding: ${spacing()};
        `;

        export const Title = styled.h2`
          padding-bottom: 0.01in;
          margin-bottom: 0.01in;
          font-size: 1em;
          font-weight: bold;
        `;
      }
    }
  }
}

export const MockImage = () => (
  <img src="https://placedog.net/550/230?random" />
);
